import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  LockOutlined,
  QuestionOutlined,
  SaveOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { ColoredTag } from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityType,
  DataLogStatus,
  ViesStatus,
  TaxRateStatus,
} from 'store/app/enums';
import { getDisplayStatusLabel } from 'utils/display-enum-label';

interface StatusGetterProps {
  status?: string;
  noDraft?: boolean;
  smaller?: boolean; // This will be converted to $smaller for styled-components
}

export const StatusGetter: React.FC<StatusGetterProps> = ({
  status,
  noDraft,
  smaller,
}) => {
  const { t } = useTranslation();
  
  // Normalize status - trim whitespace and handle empty values
  const normalizedStatus = status ? String(status).trim() : '';
  const valueToCheck =
    normalizedStatus && DataLogStatus[normalizedStatus] ? DataLogStatus[normalizedStatus] : normalizedStatus;

  // Helper function to determine the span id based on status source
  const getSpanId = (statusValue: string, originalStatus: string) => {
    const origStatus = originalStatus ? String(originalStatus).trim() : '';
    if (DataLogStatus[origStatus]) return 'status';
    if (TaxRateStatus[origStatus]) return 'TaxRateStatus';
    if (ViesStatus[origStatus]) return 'ViesStatus';
    if (ActivityType[origStatus]) return 'ActivityType';
    return 'status'; // fallback for unknown statuses
  };

  // Handle "Not Started" status (empty or NOT_STARTED)
  // Check this before the main switch statement
  // "Not Started" should always be shown, even when noDraft is true
  // DataLogStatus.NOT_STARTED = 'Not Started' (the string value)
  const isEmptyStatus = !normalizedStatus || normalizedStatus === '' || normalizedStatus === undefined || normalizedStatus === null;
  const isNotStartedStatus = normalizedStatus === 'NOT_STARTED' || 
                             normalizedStatus === DataLogStatus.NOT_STARTED || 
                             valueToCheck === DataLogStatus.NOT_STARTED || 
                             valueToCheck === 'Not Started';
  
  if (isEmptyStatus || isNotStartedStatus) {
    return (
      <ColoredTag
        color="var(--color-text-muted, #5a6a6a)" //@ts-ignore
        bgcolor="var(--color-surface, #FFFFFF)"
        bordered={true}
        bordercolor="var(--color-border, #D7D7D7)"
        icon={<MinusCircleOutlined />}
        $smaller={smaller}
      >
        <span id="status">{t('main.notStarted')}</span>
      </ColoredTag>
    );
  }

  if (normalizedStatus && normalizedStatus != '') {
    switch (valueToCheck) {
      case DataLogStatus.SAVED:
      case TaxRateStatus.ARCHIVED:
        return (
          <ColoredTag
            color="var(--color-status-info, #054581)" //@ts-ignore
            bgcolor="var(--color-status-info-bg, #E1EDF6)"
            bordered={true}
            bordercolor="#C2D3DF"
            icon={<SaveOutlined />}
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, normalizedStatus)}>
              {getDisplayStatusLabel(valueToCheck, t)}
            </span>
          </ColoredTag>
        );
      case DataLogStatus.AWAITING_APPROVAL:
      case DataLogStatus.AWAITING_CLIENT_APPROVAL:
        return (
          <ColoredTag
            color="var(--color-status-warning, #C38A20)" //@ts-ignore
            bgcolor="var(--color-status-warning-bg, #FFF5E2)"
            bordered={true}
            bordercolor="#FFBC3E"
            icon={<ClockCircleOutlined />}
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, normalizedStatus)}>
              {getDisplayStatusLabel(valueToCheck, t)}
            </span>
          </ColoredTag>
        );
      case DataLogStatus.CANCELLED:
      case ViesStatus.ERROR:
      case ViesStatus.INVALID:
      case TaxRateStatus.DELETED:
        return (
          <ColoredTag
            color="var(--color-status-danger, #D13131)" //@ts-ignore
            bgcolor="var(--color-status-danger-bg, #FCE7E7)"
            bordered={true}
            icon={
              <CloseOutlined
                style={{ color: 'var(--color-status-danger, #D13131)' }}
              />
            }
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, normalizedStatus)}>
              {getDisplayStatusLabel(valueToCheck, t)}
            </span>
          </ColoredTag>
        );
      case DataLogStatus.SUBMITTED:
      case DataLogStatus.APPROVED:
      case DataLogStatus.INTERNAL_APPROVED:
      case DataLogStatus.CLIENT_APPROVED:
      case ViesStatus.VALID:
      case TaxRateStatus.ACTIVE:
        return (
          <ColoredTag
            color="var(--color-status-success, #058181)" //@ts-ignore
            bgcolor="var(--color-status-success-bg, #E9F6F7)"
            bordered={true}
            bordercolor="var(--color-border, #dbeae3)"
            icon={<CheckOutlined />}
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, normalizedStatus)}>
              {getDisplayStatusLabel(valueToCheck, t)}
            </span>
          </ColoredTag>
        );
      case DataLogStatus.LOCKED:
        return (
          <ColoredTag
            color="var(--color-text-muted, #5a6a6a)" //@ts-ignore
            bgcolor="var(--color-surface-raised, #EAEAEA)"
            bordered={true}
            bordercolor="var(--color-border, #D7D7D7)"
            icon={<LockOutlined />}
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, normalizedStatus)}>
              {getDisplayStatusLabel(valueToCheck, t)}
            </span>
          </ColoredTag>
        );
      case DataLogStatus.NOT_SAVED:
        return (
          <ColoredTag
            color="var(--color-text-muted, #5a6a6a)" //@ts-ignore
            bgcolor="var(--color-surface-raised, #EAEAEA)"
            bordered={true}
            bordercolor="var(--color-border, #D7D7D7)"
            icon={<SaveOutlined />}
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, status)}>{t('main.draft')}</span>
          </ColoredTag>
        );
      case DataLogStatus.EXCEPTIONS_MODIFIED:
        return (
          <ColoredTag
            color="var(--color-text-muted, #5a6a6a)" //@ts-ignore
            bgcolor="var(--color-surface-raised, #EAEAEA)"
            bordered={true}
            bordercolor="var(--color-border, #D7D7D7)"
            icon={<SaveOutlined />}
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, status)}>
              {getDisplayStatusLabel(DataLogStatus.EXCEPTIONS_MODIFIED, t)}
            </span>
          </ColoredTag>
        );
      case ViesStatus.EMPTY:
      case ViesStatus.SYSTEM_UNAVAILABLE:
      case ViesStatus.MISSING:
        return (
          <ColoredTag
            color="var(--color-text-muted, #5a6a6a)" //@ts-ignore
            bgcolor="var(--color-surface-raised, #EAEAEA)"
            bordered={true}
            bordercolor="var(--color-border, #D7D7D7)"
            icon={
              <QuestionOutlined
                style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
              />
            }
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, normalizedStatus)}>
              {getDisplayStatusLabel(valueToCheck, t)}
            </span>
          </ColoredTag>
        );
      default:
        return (
          //@ts-ignore
          <ColoredTag
            color="var(--color-text-base, var(--color-text-dark, #042a2a))" //@ts-ignore
            bgcolor={
              !!ActivityType[status]
                ? 'var(--color-status-warning-bg, #FFF5E2)'
                : 'var(--color-surface, #ffffff)'
            }
            $smaller={smaller}
          >
            <span id={getSpanId(valueToCheck, status)}>
              {ActivityType[status] ? ActivityType[status] : status}
            </span>
          </ColoredTag>
        );
    }
  }
  // This should not be reached if status is empty (handled above)
  // But keeping as fallback for backward compatibility
  if (noDraft) return null;
  return (
    <ColoredTag
      color="var(--color-text-muted, #5a6a6a)" //@ts-ignore
      bgcolor="var(--color-surface-raised, #EAEAEA)"
      bordered={true}
      bordercolor="var(--color-border, #D7D7D7)"
      icon={<MinusCircleOutlined />}
      $smaller={smaller}
    >
      <span id="status">{t('main.notStarted') || 'Not Started'}</span>
    </ColoredTag>
  );
};
